import { ModalProps } from '@mui/material';
import { useCallback, useEffect, useState } from 'react';

import { ApiWorkflowDialogSteps } from './enums';
import { useApiWorkflowDialogButtons } from './useApiWorkflowDialogButtons';
import { computeDialogSize } from '../../dialogHelpers';
import { ApiWorkflowDialogProps } from '../../types';

export interface UseApiWorkflowDialogProps
  extends Pick<
    ApiWorkflowDialogProps,
    | 'dialogMessages'
    | 'isValidForSubmit'
    | 'isInitialLoading'
    | 'initialLoadingError'
    | 'isSubmitting'
    | 'submittingError'
    | 'saveButtonColor'
    | 'onClose'
    | 'onSubmit'
    | 'onRedirectAfterSubmit'
    | 'onRetryInitialLoadingFetch'
    | 'onRetrySubmit'
    | 'successCountdownTime'
    | 'hideDisplayButtonsOnSuccess'
    | 'closeOnBackdropClick'
    | 'size'
  > {
  /** Whether the dialog should show the confirmation step */
  shouldShowConfirmationStep?: boolean;
}

export const useApiWorkflowDialog = ({
  dialogMessages,
  isValidForSubmit,
  isInitialLoading,
  initialLoadingError,
  isSubmitting,
  submittingError,
  saveButtonColor = 'primary',
  onClose,
  onSubmit,
  onRedirectAfterSubmit,
  onRetryInitialLoadingFetch,
  onRetrySubmit,
  successCountdownTime,
  shouldShowConfirmationStep,
  hideDisplayButtonsOnSuccess = false,
  closeOnBackdropClick = false,
  size,
}: UseApiWorkflowDialogProps) => {
  const [currentStep, setCurrentStep] = useState(ApiWorkflowDialogSteps.UserEntry);

  // Bridges the render gap between setCurrentStep(UserSubmitted) and isSubmitting
  // becoming true: without this, ApiWorkflowDialogContent sees UserSubmitted+!isSubmitting
  // for one render and flashes the success view before the spinner appears.
  const [isBeforeFirstPending, setIsBeforeFirstPending] = useState(false);
  useEffect(() => {
    if (isSubmitting) setIsBeforeFirstPending(false);
  }, [isSubmitting]);
  // Fallback: clear the bridge after one event-loop tick if isSubmitting never
  // became true (e.g. synchronous or mocked mutations in tests).  The cleanup
  // cancels the timer when isSubmitting arrives first (normal async path).
  useEffect(() => {
    if (!isBeforeFirstPending || isSubmitting) return;
    const id = setTimeout(() => setIsBeforeFirstPending(false), 0);
    return () => clearTimeout(id);
  }, [isBeforeFirstPending, isSubmitting]);

  const effectiveIsSubmitting = isSubmitting || isBeforeFirstPending;

  const onClickConfirm = useCallback(() => {
    if (onSubmit) {
      onSubmit();
    }
    setIsBeforeFirstPending(true);
    setCurrentStep(ApiWorkflowDialogSteps.UserSubmitted);
  }, [onSubmit]);

  const onClickSubmit = useCallback(() => {
    if (shouldShowConfirmationStep) {
      // When the user clicks the submit button, the dialog should move to the confirmation step
      setCurrentStep(ApiWorkflowDialogSteps.UserConfirmation);
    } else {
      onClickConfirm();
    }
  }, [onClickConfirm, shouldShowConfirmationStep]);

  const onClickClose: ModalProps['onClose'] = (_, reason) => {
    if (reason === 'backdropClick' && !closeOnBackdropClick) {
      return;
    }
    onClose?.();
  };

  const onClickNotConfirm = () => {
    setCurrentStep(ApiWorkflowDialogSteps.UserEntry);
  };

  const hideActionButtons =
    hideDisplayButtonsOnSuccess &&
    currentStep === ApiWorkflowDialogSteps.UserSubmitted &&
    !submittingError &&
    !effectiveIsSubmitting;

  const dialogSizeOverride =
    currentStep === ApiWorkflowDialogSteps.UserSubmitted &&
    !submittingError &&
    !effectiveIsSubmitting
      ? 'medium'
      : size;

  const dialogSizeSx = computeDialogSize(dialogSizeOverride || 'medium');

  const { saveButton, cancelButton } = useApiWorkflowDialogButtons({
    dialogMessages,
    currentStep,
    isValidForSubmit: Boolean(isValidForSubmit),
    isInitialLoading: Boolean(isInitialLoading),
    initialLoadingError,
    isSubmitting: effectiveIsSubmitting,
    submittingError,
    saveButtonColor,
    onClose,
    onConfirm: onClickConfirm,
    onNotConfirm: onClickNotConfirm,
    onSubmit: onClickSubmit,
    onRedirectAfterSubmit,
    onRetryInitialLoadingFetch,
    onRetrySubmittingFetch: onRetrySubmit,
    successCountdownTime,
  });

  const isCloseDisabled =
    currentStep === ApiWorkflowDialogSteps.UserSubmitted && effectiveIsSubmitting;

  return {
    dialogSizeSx,
    currentStep,
    isCloseDisabled,
    saveButton,
    cancelButton,
    onClickSubmit,
    hideActionButtons,
    onClickClose,
    effectiveIsSubmitting,
  };
};
